/**
 * Utility helpers for navigating the v0.2 resolved model shape from the UI.
 *
 * These are NOT compatibility shims — they're concise extractors that
 * the UI uses instead of inlining repeated logic. Each helper works on
 * the v0.2 shape directly.
 */

import type {
  DependencyEdge,
  Surface,
  Interface,
  Module,
  StableId,
  TypeDef,
} from "./model.js";

/**
 * Split a merged identifier into space-separated words so the UI can
 * wrap it onto multiple lines and screen readers pronounce it sensibly.
 *
 * Rules applied (Spec §5.1 humanization):
 *   1. Split at `_`, `-`, and whitespace separators
 *   2. Camel boundary            ("processManager" → "Process Manager")
 *   3. Acronym → word boundary   ("APIKey" → "API Key")
 *   4. Digits stay glued         ("2FA" stays one word)
 *   5. Title-case each word; an all-uppercase acronym is preserved verbatim
 *
 * Caseless scripts (CJK) have no boundaries and pass through unchanged.
 * `humanizeLabel` is the canonical alias for this function.
 */
export function splitCasedName(name: string): string {
  if (!name) return name;
  const chars = [...name]; // iterate by code point (astral-safe)
  const words: string[] = [];
  let cur = "";
  const flush = (): void => {
    if (cur) words.push(cur);
    cur = "";
  };
  for (let i = 0; i < chars.length; i++) {
    const c = chars[i]!;
    if (c === "_" || c === "-" || /\s/.test(c)) {
      flush();
      continue;
    }
    if (cur) {
      const prev = cur[cur.length - 1]!;
      if (isLowerCh(prev) && isUpperCh(c)) flush();
      else if (isUpperCh(prev) && isUpperCh(c) && isLowerCh(chars[i + 1] ?? "")) {
        // Acronym → word boundary, EXCEPT a plural acronym (`IDs`, `URLs`, `APIs`):
        // a lone trailing `s` after the run is a suffix, not a new word — keep it.
        const pluralS = chars[i + 1] === "s" && !isLowerCh(chars[i + 2] ?? "");
        if (!pluralS) flush();
      }
    }
    cur += c;
  }
  flush();
  return words.map(titleCaseWord).join(" ");
}

/** Canonical name for the §5.1 display humanizer. */
export const humanizeLabel = splitCasedName;

function titleCaseWord(w: string): string {
  // An all-uppercase word carrying a letter is an acronym — leave it verbatim.
  if (w.length >= 2 && w === w.toUpperCase() && w !== w.toLowerCase()) return w;
  const first = [...w][0] ?? "";
  return first.toUpperCase() + w.slice(first.length);
}
function isUpperCh(c: string): boolean {
  return c !== "" && c === c.toUpperCase() && c !== c.toLowerCase();
}
function isLowerCh(c: string): boolean {
  return c !== "" && c === c.toLowerCase() && c !== c.toUpperCase();
}

/**
 * The five built-in base kinds every type chain bottoms out in. Used
 * to surface bare-kind entries (e.g. `module`, `interface`) in the
 * type browser alongside user-declared and imported types.
 */
export const BASE_TYPE_NAMES = [
  "module",
  "surface",
  "interface",
  "process",
  "view",
] as const;

/** Library label used for the synthetic base-kind stubs. */
export const BUILTIN_LIBRARY = "(built-in)";

export type BaseType = (typeof BASE_TYPE_NAMES)[number];

export function isBaseType(s: string): s is BaseType {
  return (BASE_TYPE_NAMES as readonly string[]).includes(s);
}

/**
 * Walk a type's parentType chain, returning [base, ..., parent, self].
 * Stops at the first built-in base kind or when no further TypeDef
 * exists (treats unknown parents as base-kind-shaped strings).
 */
export function resolveTypeChain(
  type: TypeDef,
  byName: ReadonlyMap<string, TypeDef>,
): string[] {
  const chain: string[] = [type.name];
  let cur: TypeDef | undefined = type;
  // Cap iterations against accidental cycles in user-authored types.
  for (let i = 0; i < 32; i++) {
    if (!cur) break;
    const parent = cur.parentType;
    if (isBaseType(parent)) {
      chain.unshift(parent);
      return chain;
    }
    const parentDecl = byName.get(parent);
    if (!parentDecl) {
      chain.unshift(parent);
      return chain;
    }
    chain.unshift(parent);
    cur = parentDecl;
  }
  return chain;
}

export interface TypeClassification {
  readonly baseType: BaseType;
  readonly chain: readonly string[];
}

export function classifyType(
  type: TypeDef,
  byName: ReadonlyMap<string, TypeDef>,
): TypeClassification {
  // Synthetic base-kind stubs self-reference their own name as
  // parentType (e.g. {name:"module", parentType:"module"}). Short-
  // circuit to avoid resolveTypeChain producing ["module","module",…].
  if (isBaseType(type.name) && type.parentType === type.name) {
    return { baseType: type.name, chain: [type.name] };
  }
  const chain = resolveTypeChain(type, byName);
  const root = chain[0]!;
  const baseType = isBaseType(root) ? root : "module";
  return { baseType, chain };
}

export const BASE_TYPE_LABEL: Record<BaseType, string> = {
  module: "Modules",
  surface: "Surfaces",
  interface: "Interfaces",
  process: "Processes",
  view: "Views",
};

export const BASE_TYPE_COLOR: Record<BaseType, string> = {
  module: "text-emerald-300",
  surface: "text-purple-300",
  interface: "text-sky-300",
  process: "text-amber-300",
  view: "text-rose-300",
};

/** One-letter mark used by the sidebar list badge. */
export const BASE_TYPE_MARK: Record<BaseType, string> = {
  module: "M",
  surface: "F",
  interface: "I",
  process: "P",
  view: "V",
};

/**
 * Base kinds the type browser actually surfaces. Surfaces render inline
 * inside a parent module; processes and views are diagram-time
 * concepts with no widget shape — none of the three have anything
 * useful to preview, so the browser hides them entirely. Module and
 * interface are the only kinds that get listed and previewed.
 */
export const PREVIEWABLE_BASE_KINDS = ["module", "interface"] as const;

/**
 * Prepend synthetic TypeDef entries for the previewable base kinds
 * (`module`, `interface`) so the browser lists them alongside
 * user-declared and imported types. Each stub self-references its
 * own name as `parentType` — `classifyType` short-circuits the
 * chain to `[name]`.
 */
export function withBaseTypeStubs(
  types: readonly TypeDef[],
): readonly TypeDef[] {
  const existing = new Set(types.map((t) => t.name));
  const stubs: TypeDef[] = PREVIEWABLE_BASE_KINDS
    .filter((name) => !existing.has(name))
    .map((name) => ({
      name,
      parentType: name,
      library: BUILTIN_LIBRARY,
    }));
  return [...stubs, ...types];
}

/**
 * Filter a type list down to entries whose resolved base kind is one
 * the browser surfaces. Drops surface/process/view types.
 */
export function filterPreviewableTypes(
  types: readonly TypeDef[],
): readonly TypeDef[] {
  const byName = new Map<string, TypeDef>();
  for (const t of types) byName.set(t.name, t);
  const previewable = new Set<string>(PREVIEWABLE_BASE_KINDS);
  return types.filter((t) => previewable.has(classifyType(t, byName).baseType));
}

/**
 * Per-Module qualified name cache. Populated by `stampQualifiedKeys`
 * after a model load; consulted by `moduleKey` as a fallback when the
 * module has no `#id`.
 *
 * Without this, nested no-id modules would key by bare name (e.g.
 * `OrdersTable`) while edges reference the qualified path
 * (`DataSchemas.OrdersTable`) — the layout's edge filter then drops
 * every cross-reference inside a nested space, making arrows
 * invisible.
 *
 * WeakMap so entries are GC'd when the model reloads and the old
 * Module instances are dropped.
 */
export const qualifiedKeyByModule = new WeakMap<Module, string>();

/**
 * Walk the module tree and record each module's qualified name
 * (`Parent.Child.Grandchild`) keyed by Module reference. Idempotent —
 * safe to call repeatedly on the same tree (overwrites with the same
 * value). Call once whenever a fresh model is loaded.
 */
export function stampQualifiedKeys(roots: readonly Module[]): void {
  // v0.11 (D2): a spaced module's key is `Space::Qualified.Path` so two
  // spaces' same-named modules stay distinct board/index entities; spaceless
  // models keep the byte-identical bare key. Children inherit the root's
  // space when they don't carry their own (one landed space per subtree).
  const visit = (m: Module, parentQual: string | undefined, space: string | undefined): void => {
    const qual = parentQual ? `${parentQual}.${m.name}` : m.name;
    const s = m.space ?? space;
    qualifiedKeyByModule.set(m, s ? `${s}::${qual}` : qual);
    for (const c of m.children) visit(c, qual, s);
  };
  for (const r of roots) visit(r, undefined, undefined);
}

/**
 * Stable identity key for a module. Prefers `#id` when present;
 * otherwise uses the qualified path stamped by `stampQualifiedKeys`;
 * finally falls back to bare name. The qualified-path fallback keeps
 * `moduleKey()` aligned with `edgeFromKey()`/`edgeToKey()`, which
 * always use the dotted path when no id is present.
 */
export function moduleKey(m: Module): string {
  return m.id ?? qualifiedKeyByModule.get(m) ?? m.name;
}

/** Convenience: serialize an edge endpoint to a string key. v0.11 (D2a):
 *  the endpoint's landed space folds into the no-id key, matching
 *  `moduleKey`'s `Space::Qualified.Path` form so the edge↔node join stays
 *  exact for spaced models. */
export function edgeFromKey(e: DependencyEdge): string {
  if (e.fromModuleId) return e.fromModuleId;
  const path = e.fromModulePath.join(".");
  return e.fromSpace ? `${e.fromSpace}::${path}` : path;
}

export function edgeToKey(e: DependencyEdge): string {
  if (e.toModuleId) return e.toModuleId;
  const path = e.toModulePath.join(".");
  return e.toSpace ? `${e.toSpace}::${path}` : path;
}

/** Look up a field by dotted path. Returns undefined if absent. */
export function getField(m: Module | Surface | Interface, path: string): unknown {
  const segs = path.split(".");
  // `fields` is non-optional on the wire, but a partial parse / hand-built
  // stub can omit it — guard so the shared table/class/enum detail lookups
  // never throw "fields is not iterable" on incomplete data.
  for (const f of m.fields ?? []) {
    if (
      segs.length === f.path.length &&
      segs.every((s, i) => s === f.path[i])
    ) {
      return f.value;
    }
  }
  return undefined;
}

/** Look up an aspect by dotted key. Returns undefined if absent. */
export function getAspect(
  m: Module | Surface | Interface,
  path: string
): unknown {
  const segs = path.split(".");
  for (const l of m.aspects) {
    if (
      segs.length === l.path.length &&
      segs.every((s, i) => s === l.path[i])
    ) {
      return l.value;
    }
  }
  return undefined;
}

/**
 * Best-effort string extraction from a Value-like JSON. The server may
 * serialize values as `{kind:"string",text:"..."}` or as primitives —
 * this normalizes both shapes.
 */
export function valueToString(v: unknown): string | undefined {
  if (v === undefined || v === null) return undefined;
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  if (typeof v === "object") {
    const obj = v as { text?: string; value?: unknown; kind?: string; items?: unknown[] };
    if (obj.kind === "list" && Array.isArray(obj.items)) {
      // Engine parity (description.ts): list values render comma-joined.
      return obj.items.map((it) => valueToString(it) ?? "").join(", ");
    }
    if (typeof obj.text === "string") return obj.text;
    if (obj.value !== undefined) return valueToString(obj.value);
  }
  return undefined;
}

export function getFieldString(
  m: Module | Surface | Interface,
  path: string
): string | undefined {
  return valueToString(getField(m, path));
}

export function getAspectString(
  m: Module | Surface | Interface,
  path: string
): string | undefined {
  return valueToString(getAspect(m, path));
}

/** Get the team field as a string (no cascade walk yet). */
export function getTeam(m: Module | Surface): string | undefined {
  return getFieldString(m, "team");
}

/** Get the domain aspect as a string (no cascade walk yet). */
export function getDomain(m: Module | Surface): string | undefined {
  return getAspectString(m, "domain");
}

/**
 * Flatten a module's API surface — returns all interfaces directly on
 * the module plus every interface inside any (possibly nested) surface.
 */
export function flattenInterfaces(m: Module | Surface): Interface[] {
  const out: Interface[] = [...m.interfaces];
  for (const f of m.surfaces) {
    out.push(...flattenInterfaces(f));
  }
  return out;
}

/**
 * Walk the module tree depth-first, calling `visit` on every module.
 */
export function walkModules(
  modules: readonly Module[],
  visit: (m: Module, parent?: Module) => void,
  parent?: Module
): void {
  for (const m of modules) {
    visit(m, parent);
    walkModules(m.children, visit, m);
  }
}

/** All modules flattened to a single array, depth-first. */
export function allModules(roots: readonly Module[]): Module[] {
  const out: Module[] = [];
  walkModules(roots, (m) => out.push(m));
  return out;
}

/** Find a module by id or fully-qualified name. */
export function findModule(
  roots: readonly Module[],
  key: StableId
): Module | undefined {
  for (const m of allModules(roots)) {
    if (m.id === key || m.name === key) return m;
  }
  return undefined;
}

/**
 * Flatten the nested module tree into a flat list with `parentId`
 * references, the shape the board layout expects. Each module gets a
 * synthetic `parentId` set to its parent's `moduleKey()` (id or name).
 */
export function flattenModulesWithParent(
  roots: readonly Module[]
): Array<Module & { parentId?: StableId }> {
  // Ensure originals carry qualified keys, then propagate each entry
  // onto the shallow clones we hand back. The clones are distinct
  // object references — without this re-stamp, the WeakMap lookup in
  // `moduleKey` misses on the clone and falls back to bare name,
  // breaking edge-to-module key matching inside nested spaces.
  stampQualifiedKeys(roots);
  const out: Array<Module & { parentId?: StableId }> = [];
  function visit(m: Module, parentKeyValue: StableId | undefined): void {
    const clone = { ...m, parentId: parentKeyValue };
    const qual = qualifiedKeyByModule.get(m);
    if (qual !== undefined) qualifiedKeyByModule.set(clone, qual);
    out.push(clone);
    const myKey = moduleKey(m) as StableId;
    for (const c of m.children) visit(c, myKey);
  }
  for (const r of roots) visit(r, undefined);
  return out;
}
